function toFiniteOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function mergeSwapValue(existing, incoming) {
  const current = toFiniteOrNull(existing);
  const next = toFiniteOrNull(incoming);
  if (next == null) return current;
  if (next === 0 && current != null && current !== 0) return current;
  return next;
}

function withComputedTradeFields(trade) {
  const broker1Profit = toFiniteOrNull(trade?.broker1Profit);
  const broker2Profit = toFiniteOrNull(trade?.broker2Profit);
  const broker1Swap = toFiniteOrNull(trade?.broker1Swap);
  const broker2Swap = toFiniteOrNull(trade?.broker2Swap);
  const swap = (broker1Swap || 0) + (broker2Swap || 0);

  return {
    ...trade,
    broker1Profit,
    broker2Profit,
    broker1Swap,
    broker2Swap,
    swap,
    totalSwap: swap,
    totalProfit: (broker1Profit || 0) + (broker2Profit || 0) + swap,
  };
}

function mergeTradeSnapshotMetrics(nextTrade, previousTrade) {
  if (!previousTrade) {
    return withComputedTradeFields(nextTrade);
  }

  const mergedTrade = { ...nextTrade };
  if (mergedTrade.broker1Profit == null && previousTrade.broker1Profit != null) {
    mergedTrade.broker1Profit = previousTrade.broker1Profit;
  }
  if (mergedTrade.broker2Profit == null && previousTrade.broker2Profit != null) {
    mergedTrade.broker2Profit = previousTrade.broker2Profit;
  }
  if (mergedTrade.broker1Swap == null && previousTrade.broker1Swap != null) {
    mergedTrade.broker1Swap = previousTrade.broker1Swap;
  }
  if (mergedTrade.broker2Swap == null && previousTrade.broker2Swap != null) {
    mergedTrade.broker2Swap = previousTrade.broker2Swap;
  }

  return withComputedTradeFields(mergedTrade);
}

function getTicketCandidates(ticket) {
  const candidates = [];
  const ticketStr = String(ticket ?? "").trim();
  if (ticketStr) {
    candidates.push(ticketStr);

    const ticketNum = Number(ticketStr);
    if (Number.isFinite(ticketNum)) {
      const numericKey = String(ticketNum);
      if (!candidates.includes(numericKey)) {
        candidates.push(numericKey);
      }
    }
  }

  return candidates;
}

function getLookupPart(value) {
  return String(value ?? "").trim();
}

function addLookupCandidate(map, key, payload) {
  if (!key) return;

  const existing = map.get(key);
  if (!existing) {
    map.set(key, payload);
    return;
  }

  if (Array.isArray(existing)) {
    existing.push(payload);
    return;
  }

  map.set(key, [existing, payload]);
}

function getUniqueLookupCandidate(map, key) {
  if (!map || !key) return null;

  const candidate = map.get(key);
  if (!candidate) return null;
  if (Array.isArray(candidate)) {
    return candidate.length === 1 ? candidate[0] : null;
  }
  return candidate;
}

function defaultPayloadBuilder(entry) {
  return {
    ticket: entry?.ticket,
    symbol: entry?.symbol ?? entry?.Symbol ?? "",
    brokerId: entry?.brokerId,
    brokerPosition: entry?.brokerPosition,
    profit: toFiniteOrNull(entry?.profit ?? entry?.Profit),
    swap: toFiniteOrNull(entry?.swap ?? entry?.Swap),
  };
}

function buildBrokerScopedLookup(entries = [], payloadBuilder = defaultPayloadBuilder) {
  const byTicketAndBroker = new Map();
  const byTicketAndBrokerId = new Map();
  const bySymbolAndBroker = new Map();
  const bySymbolAndBrokerId = new Map();

  for (const entry of entries) {
    const payload = payloadBuilder(entry);
    const brokerPosition = Number(payload?.brokerPosition ?? entry?.brokerPosition);
    if (!Number.isFinite(brokerPosition)) continue;
    const brokerId = getLookupPart(payload?.brokerId ?? entry?.brokerId);

    const ticket = payload?.ticket ?? entry?.ticket;
    for (const ticketKey of getTicketCandidates(ticket)) {
      byTicketAndBroker.set(`${ticketKey}:${brokerPosition}`, payload);
      if (brokerId) {
        byTicketAndBrokerId.set(`${ticketKey}:${brokerId}`, payload);
      }
    }

    const symbol = getLookupPart(payload?.symbol ?? entry?.symbol ?? entry?.Symbol);
    if (symbol) {
      addLookupCandidate(bySymbolAndBroker, `${symbol}:${brokerPosition}`, payload);
      if (brokerId) {
        addLookupCandidate(bySymbolAndBrokerId, `${symbol}:${brokerId}`, payload);
      }
    }
  }

  return {
    byTicketAndBroker,
    byTicketAndBrokerId,
    bySymbolAndBroker,
    bySymbolAndBrokerId,
  };
}

function findBrokerScopedPayload(trade, brokerPosition, lookup) {
  if (!trade || !lookup) return null;

  const ticket = brokerPosition === 1 ? trade.broker1Ticket : trade.broker2Ticket;
  const symbol = brokerPosition === 1 ? trade.broker1Symbol : trade.broker2Symbol;
  const brokerId = getLookupPart(brokerPosition === 1 ? trade.broker1Id : trade.broker2Id);
  const ticketCandidates = getTicketCandidates(ticket);

  for (const ticketKey of ticketCandidates) {
    if (brokerId) {
      const directByBrokerId = lookup.byTicketAndBrokerId?.get(`${ticketKey}:${brokerId}`);
      if (directByBrokerId) return directByBrokerId;
    }

    const direct = lookup.byTicketAndBroker.get(`${ticketKey}:${brokerPosition}`);
    if (direct) return direct;
  }

  if (ticketCandidates.length > 0) {
    return null;
  }

  const symbolKey = getLookupPart(symbol);
  if (!symbolKey) {
    return null;
  }

  if (brokerId) {
    const directByBrokerId = getUniqueLookupCandidate(
      lookup.bySymbolAndBrokerId,
      `${symbolKey}:${brokerId}`,
    );
    if (directByBrokerId) return directByBrokerId;
  }

  return getUniqueLookupCandidate(lookup.bySymbolAndBroker, `${symbolKey}:${brokerPosition}`);
}

export {
  toFiniteOrNull,
  mergeSwapValue,
  withComputedTradeFields,
  mergeTradeSnapshotMetrics,
  buildBrokerScopedLookup,
  findBrokerScopedPayload,
};
